import {
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import {
  BillingProviderDTO,
  NPISearchItemDTO,
  ProviderSuggestionDTO,
  UserDTO,
} from 'dtos';
import {
  getProvidersByParams,
} from 'pages/Dashboard/pages/Billing/api';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  getApiClaimGetBillingProviders,
  getApiClaimGetReferringProviderSuggestionsClaimId,
  getApiEncounterGetReferringProviderSuggestions,
  getApiUserGetAllUsersByTitleTitle,
} from 'endpoints';
import {
  normalizeUsers,
} from 'pages/Dashboard/utils/helper';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  ProviderSearchParams,
} from 'pages/Dashboard/types/provider';

type UseGetUsersOptions = {
  onlyDoctors?: boolean;
  sortBySelf?: boolean;
  enabled?: boolean;
};

// @deprecated
export function useGetBillingProviders(): UseQueryResult<BillingProviderDTO[], Error> {
  return usePrefetchedData<BillingProviderDTO[]>({
    key: ['billingProviders'],
    fetchFn: async () => getApiClaimGetBillingProviders(),
  });
}

export function useGetUsers(
  {
    onlyDoctors = true,
    sortBySelf = true,
    enabled = true,
  }: UseGetUsersOptions = {},
): UseQueryResult<UserDTO[], Error> {
  const userInfo = useUserInfo();
  const { user } = userInfo ?? {};
  const doctor = onlyDoctors ? 'doctor' : '';
  const shouldFetchUsers = enabled && !isNil(userInfo);

  return usePrefetchedData<UserDTO[]>({
    key: ['doctorList', doctor],
    fetchFn: async () => {
      const providers = await getApiUserGetAllUsersByTitleTitle(doctor);
      return normalizeUsers(providers, user?.userId, sortBySelf);
    },
    enabled: shouldFetchUsers,
  });
}

export function useProvidersSearch(
  queryParams: ProviderSearchParams | null,
  enabled: boolean = true,
): UseQueryResult<NPISearchItemDTO[], Error> {
  return useQuery<NPISearchItemDTO[], Error>({
    queryKey: ['providers-search', values(queryParams)],
    queryFn: async () => {
      const response = await (isNil(queryParams)
        ? Promise.resolve({ result: [] })
        : getProvidersByParams(queryParams)
      );

      return response?.result ?? [];
    },
    enabled: !isNil(queryParams) && !isEmpty(queryParams) && enabled,
  });
}

export function useClaimReferringProviderSuggestions(
  claimId: number = 0,
  enabled: boolean = true,
): UseQueryResult<ProviderSuggestionDTO[], Error> {
  return useQuery<ProviderSuggestionDTO[], Error>({
    queryKey: ['claim-referring-provider-suggestions', claimId],
    queryFn: async () => getApiClaimGetReferringProviderSuggestionsClaimId(claimId),
    enabled: claimId > 0 && enabled,
  });
}

export function useEncounterReferringProviderSuggestions(
  patientId: number = 0,
  encounterId?: number | null,
  enabled: boolean = true,
): UseQueryResult<ProviderSuggestionDTO[], Error> {
  return useQuery<ProviderSuggestionDTO[], Error>({
    queryKey: ['encounter-referring-provider-suggestions', patientId, encounterId],
    queryFn: async () => getApiEncounterGetReferringProviderSuggestions({
        patientId,
        encounterId: encounterId ?? undefined,
      }),
    enabled: patientId > 0 && enabled,
  });
}
